import React, { memo, useState } from 'react';
import { Dropdown, MenuProps, Typography } from 'antd';
import { formatMoneyExport, toNumber } from '@nxsys/utils/number-helper';
import { timesExact } from '@nxsys/utils/exact-money';
import { showCurrencyByCountry } from '@nxsys/utils';
import { KidValue } from '@nxsys/store/slices/kid/types';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { AppDispatch, AppState } from '@nxsys/store';
import {
  selectKidValue,
  selectActionVisible,
  selectPreviewFormData,
} from '@nxsys/store/slices/kid/kidSlice';
import { validateEmail } from '@nxsys/utils/validator';
import { showToast } from '@nxsys/utils/toast';
import AppButton from '@nxsys/components/AppButton';
import { DownOutlined, EyeOutlined, MailOutlined } from '@ant-design/icons';
import { ArrowTightTopIcon } from '@nxsys/components/icons';
import { kidAction } from '@nxsys/store/slices/kid/kidSlice';
import PreviewModal from '../../PreviewModal';

type PreviewContentProps = {
  data?: KidValue;
};
type HeaderActionGroupPropsTypes = {
  handlePreview: (type: 'KID' | 'PAY_ILLUSTRATION') => void;
  handleSentEmail: (type: 'KID' | 'PAY_ILLUSTRATION') => void;
};

const HeaderActionGroup = ({
  handlePreview,
  handleSentEmail,
}: HeaderActionGroupPropsTypes) => {
  const actionVisible = useSelector(selectActionVisible);
  const formData = useSelector(selectPreviewFormData);

  const previewMenuItems: MenuProps['items'] = [
    {
      key: 'preview-kid',
      label: 'KID',
      onClick: () => handlePreview('KID'),
    },
    {
      key: 'preview-pay-illustration',
      label: 'Pay Illustration',
      onClick: () => handlePreview('PAY_ILLUSTRATION'),
    },
  ];

  const sendMenuItems: MenuProps['items'] = [
    {
      key: 'send-kid',
      label: 'KID',
      icon: <MailOutlined />,
      onClick: () => handleSentEmail('KID'),
    },
    {
      key: 'send-pay-illustration',
      label: 'Pay Illustration',
      icon: <MailOutlined />,
      onClick: () => handleSentEmail('PAY_ILLUSTRATION'),
    },
  ];

  return (
    <div className="w-full flex justify-end footer-actions gap-1">
      <Dropdown
        key="preview"
        menu={{ items: previewMenuItems }}
        trigger={['click']}
        disabled={!actionVisible}
      >
        <AppButton
          name="View"
          variant="outlined"
          icon={<EyeOutlined />}
          rightIcon={<DownOutlined />}
          onClick={() => {}}
          disabled={!actionVisible}
        />
      </Dropdown>
      <Dropdown
        key="send"
        menu={{ items: sendMenuItems }}
        trigger={['click']}
        disabled={!actionVisible || !formData?.emailId}
      >
        <AppButton
          name="Send"
          type="primary"
          icon={<ArrowTightTopIcon color="#00795B" />}
          rightIcon={<DownOutlined />}
          iconPosition="start"
          onClick={() => {}}
          disabled={!actionVisible || !formData?.emailId}
        />
      </Dropdown>
    </div>
  );
};

// Utility function to safely convert string or number to number

const PreviewContent: React.FC<PreviewContentProps> = memo(
  ({ data }: PreviewContentProps) => {
    const reduxData = useSelector(selectKidValue);
    const dispatch = useDispatch<AppDispatch>();
    const formData = useSelector(selectPreviewFormData);
    const { payrollCountry } = useSelector((state: AppState) => state.appStore);
    const currencySymbol = showCurrencyByCountry(payrollCountry);
    const [previewHtml, setPreviewHtml] = useState<string | null>(null);

    const handlePreview = async (type: 'KID' | 'PAY_ILLUSTRATION') => {
      const requestParams = {
        ...formData,
        emailId: undefined,
        type: type,
      };
      const messageType = type === 'KID' ? 'KID' : 'Pay Illustration';
      try {
        const response = await dispatch(
          kidAction.previewForCis(requestParams),
        ).unwrap();
        setPreviewHtml(response?.data || '');
      } catch (error: any) {
        console.log('error', error);
        const errorMessage = error.message || `Failed to preview ${messageType}`;
        showToast({ type: 'error', message: errorMessage });
      }
    };

    const handleSentEmail = async (type: 'KID' | 'PAY_ILLUSTRATION') => {
      if (!formData?.emailId) {
        showToast({ type: 'error', message: 'Email is required!' });
        return;
      }

      if (!validateEmail(formData?.emailId || '')) {
        showToast({ type: 'error', message: 'Invalid email!' });
        return;
      }
      const requestParams = {
        ...formData,
        type: type,
      };
      const messageType = type === 'KID' ? 'KID' : 'Pay Illustration';
      try {
        await dispatch(kidAction.sendEmailForCis(requestParams)).unwrap();
        showToast({
          type: 'success',
          message: `${messageType} sent successfully!`,
        });
      } catch (error: any) {
        console.log('error', error);
        const errorMessage = error.message || `Failed to send email ${messageType}`;
        showToast({ type: 'error', message: errorMessage });
      }
    };

    // Use passed data if available, otherwise use Redux data
    const finalData = data || reduxData;

    const totalIncome =
      finalData?.payRatePerHour && finalData?.noOfHours
        ? timesExact(
            toNumber(finalData?.payRatePerHour),
            toNumber(finalData?.noOfHours),
          ) + toNumber(finalData?.constOfMaterial || 0)
        : 0;
    const taxablePay = totalIncome - toNumber(finalData?.margin);
    // Always show content, even if data is empty
    const cisDeDuction =
      finalData?.noOfHours && finalData?.payRatePerHour && finalData?.cisStatus
        ? (totalIncome - toNumber(finalData?.margin)) *
          finalData?.cisStatus?.numberValue
        : 0;

    const margin =
      finalData?.margin !== undefined
        ? formatMoneyExport(toNumber(finalData?.margin), currencySymbol)
        : formatMoneyExport(0, currencySymbol);
    const vatRate = !!finalData?.VATRegNo ? 0.2: 0;
    const vatPercent = `${vatRate * 100}%`;
    const vat = taxablePay * vatRate;
    const netPay = taxablePay - toNumber(cisDeDuction) + vat;

    return (
      <>
        <div className="flex justify-between items-center px-5 py-3 border-b border-neutral-200">
          <Typography.Title
            level={4}
            className="flex-shrink-0"
            style={{ margin: 0 }}
          >
            Preview
          </Typography.Title>
          <HeaderActionGroup
            handlePreview={handlePreview}
            handleSentEmail={handleSentEmail}
          />
        </div>
        <div className="p-5">
          <div className="border rounded-[8px] mt-[18px]">
            <ul className="table-example-pay">
              <li className="flex py-[16px] border-b">
                <span className="flex-1 pl-[24px]">
                  <span className="block font-[500] text-sm">
                    A) Total Income
                  </span>
                </span>
                <span className="w-[150px] pl-[24px] font-[500] text-sm">
                  {formatMoneyExport(totalIncome, currencySymbol)}
                </span>
                <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                  -
                </span>
              </li>
              <li className="flex py-[16px] border-b">
                <span className="flex-1 pl-[24px]">
                  <span className="block font-[500] text-sm">B) Margin</span>
                </span>
                <span className="w-[150px] pl-[24px] font-[500] text-sm">
                  {margin}
                </span>
                <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                  -
                </span>
              </li>
              <li className="flex py-[16px] border-b">
                <span className="flex-1 pl-[24px]">
                  <span className="block font-[500] text-sm">
                    C) Taxable Pay
                  </span>
                </span>
                <span className="w-[150px] pl-[24px] font-[500] text-sm">
                  {formatMoneyExport(taxablePay, currencySymbol)}
                </span>
                <span className="w-[150px] pl-[12px] font-[400] text-sm font-[500]">
                  (A-B)
                </span>
              </li>
              <li className="flex py-[16px] border-b">
                <span className="flex-1 pl-[24px]">
                  <span className="block font-[500] text-sm">
                    {`D) VAT (${vatPercent})`}
                  </span>
                </span>
                <span className="w-[150px] pl-[24px] font-[500] text-sm">
                  {formatMoneyExport(vat, currencySymbol)}
                </span>
                <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                  -
                </span>
              </li>
              <li className="flex py-[16px] border-b">
                <span className="flex-1 pl-[24px]">
                  <span className="block font-[500] text-sm">
                    E) CIS Deductions
                  </span>
                </span>
                <span className="w-[150px] pl-[24px] font-[500] text-sm">
                  {formatMoneyExport(cisDeDuction, currencySymbol)}
                </span>
                <span className="w-[150px] pl-[12px] font-[400] text-xs text-gray-500">
                  -
                </span>
              </li>
              <li className="flex py-[16px]">
                <span className="flex-1 pl-[24px]">
                  <span className="block font-[500] text-sm">F) Net Pay</span>
                </span>
                <span className="w-[150px] pl-[24px] font-[500] text-sm">
                  {formatMoneyExport(netPay, currencySymbol)}
                </span>
                <span className="w-[150px] pl-[12px] font-[500] text-sm">
                  C+D-E
                </span>
              </li>
            </ul>
          </div>
        </div>
        <PreviewModal html={previewHtml} onClose={() => setPreviewHtml(null)} />
      </>
    );
  },
);

export default PreviewContent;
